import React, { useEffect, useState } from "react";
import api from "../../api";
import { toast } from "sonner";
import { ShieldCheck, Plus, Trash2, Network, Globe, RefreshCw } from "lucide-react";

const fmtDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso || "—"; }
};

const inputCls = "border border-gray-300 rounded-sm px-3 py-2 text-[14px] outline-none focus:border-[#16345c] bg-white";

export const WhitelistTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ip, setIp] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/security/whitelist")
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Errore nel caricamento degli IP fidati"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const add = (e) => {
    e.preventDefault();
    if (!ip.trim()) { toast.error("Inserisci un indirizzo IP o un intervallo CIDR"); return; }
    setSaving(true);
    api.post("/security/whitelist", { ip: ip.trim(), note: note.trim() })
      .then((r) => {
        const unblocked = (r.data && r.data.unblocked) || [];
        toast.success(unblocked.length ? `IP fidato aggiunto · ${unblocked.length} blocco/i rimosso/i` : "IP fidato aggiunto");
        setIp(""); setNote("");
        load();
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Errore durante l'aggiunta"))
      .finally(() => setSaving(false));
  };

  const remove = (it) => {
    if (!window.confirm(`Rimuovere ${it.ip} dagli IP fidati?`)) return;
    api.delete(`/security/whitelist/${it.id}`)
      .then(() => { toast.success("IP fidato rimosso"); load(); })
      .catch(() => toast.error("Errore durante la rimozione"));
  };

  return (
    <div data-testid="whitelist-tab">
      <div className="flex items-start gap-2 mb-4 text-[13px] text-gray-600 max-w-[760px]">
        <ShieldCheck size={16} className="text-green-600 shrink-0 mt-0.5" />
        <span>Gli <b>IP fidati</b> (rete dell'ufficio, VPN aziendale, sedi note) <b>non vengono mai bloccati automaticamente</b> dal sistema di sicurezza. Puoi inserire un singolo indirizzo (es. <span className="font-mono">203.0.113.10</span>) oppure un intero intervallo di rete in formato CIDR (es. <span className="font-mono">203.0.113.0/24</span>).</span>
      </div>

      <form onSubmit={add} className="bg-white border border-gray-200 rounded-lg p-4 mb-5">
        <h3 className="text-[15px] font-black text-[#16345c] mb-3 flex items-center gap-2"><Plus size={16} /> Aggiungi IP fidato</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[12px] font-bold text-gray-700">Indirizzo IP o intervallo (CIDR)</label>
            <input value={ip} onChange={(e) => setIp(e.target.value)} data-testid="whitelist-ip-input" placeholder="es. 203.0.113.10 oppure 203.0.113.0/24" className={`${inputCls} w-[280px] font-mono`} />
          </div>
          <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
            <label className="text-[12px] font-bold text-gray-700">Nota (opzionale)</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="whitelist-note-input" placeholder="es. Rete ufficio sede Roma" className={`${inputCls} w-full`} />
          </div>
          <button type="submit" disabled={saving} data-testid="whitelist-add-btn" className="flex items-center gap-2 bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-4 py-2 rounded-sm transition-colors disabled:opacity-60">
            <Plus size={15} /> {saving ? "Aggiunta…" : "Aggiungi"}
          </button>
        </div>
      </form>

      <div className="flex items-center justify-between mb-3">
        <span className="text-[13px] font-bold text-[#16345c]">IP fidati ({items.length})</span>
        <button onClick={load} disabled={loading} data-testid="whitelist-refresh" className="flex items-center gap-2 border border-[#16345c] text-[#16345c] hover:bg-[#16345c] hover:text-white text-[13px] font-bold px-4 py-2 rounded-sm transition-colors disabled:opacity-60">
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Aggiorna
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto" data-testid="whitelist-table">
        <table className="w-full min-w-[720px]">
          <thead>
            <tr className="bg-[#16345c] text-white text-[12px] uppercase tracking-wide">
              <th className="text-left px-4 py-3 font-bold">Indirizzo / Intervallo</th>
              <th className="text-left px-4 py-3 font-bold">Tipo</th>
              <th className="text-left px-4 py-3 font-bold">Nota</th>
              <th className="text-left px-4 py-3 font-bold">Aggiunto da</th>
              <th className="text-left px-4 py-3 font-bold">Data</th>
              <th className="text-left px-4 py-3 font-bold">Azione</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} data-testid={`whitelist-row-${it.id}`} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3"><span className="font-mono text-[13px] font-bold text-[#16345c]">{it.ip}</span></td>
                <td className="px-4 py-3">
                  {it.is_range
                    ? <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700"><Network size={12} /> Intervallo</span>
                    : <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700"><Globe size={12} /> Singolo IP</span>}
                </td>
                <td className="px-4 py-3 text-[13px] text-gray-700">{it.note || "—"}</td>
                <td className="px-4 py-3 text-[13px] text-gray-700">{it.added_by_name || "—"}</td>
                <td className="px-4 py-3 text-[13px] text-gray-600 whitespace-nowrap">{fmtDateTime(it.created_at)}</td>
                <td className="px-4 py-3">
                  <button onClick={() => remove(it)} data-testid={`whitelist-delete-${it.id}`} className="flex items-center gap-1.5 text-[12px] font-bold text-red-600 border border-red-300 hover:bg-red-600 hover:text-white px-3 py-1.5 rounded-sm transition-colors">
                    <Trash2 size={13} /> Rimuovi
                  </button>
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400 text-[13px]">Nessun IP fidato configurato. Aggiungi la rete dell'ufficio per evitare blocchi accidentali.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
